
import { KeyboardKey } from "./KeyboardKey";
import { Button } from "../ui/button";
import { Delete, CheckCircle } from "lucide-react";
import { memo } from "react";
import { useGameMode } from "@/contexts/GameModeContext";
import { trackEvent } from "@/lib/analytics";

const KEYBOARD_ROWS = [
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
  ["z", "x", "c", "v", "b", "n", "m"],
];

interface KeyboardProps {
  keyboardStatus: { [key: string]: "correct" | "wrong-position" | "incorrect" | "empty" };
  onKeyPress: (key: string) => void;
  onSubmit: () => void;
  isValidating: boolean;
  currentGuess: string;
  currentWord: string;
  compactLayout?: boolean;
}

export const Keyboard = memo(({ 
  keyboardStatus, 
  onKeyPress, 
  onSubmit, 
  isValidating,
  currentGuess,
  currentWord,
  compactLayout = false
}: KeyboardProps) => {
  const { currentMode } = useGameMode();
  const isPracticeMode = currentMode === 'practice';
  const isSubmitDisabled = isValidating || currentGuess.length !== currentWord.length;

  return (
    <div className={`keyboard-container mx-auto py-1 px-1 w-full ${compactLayout ? "livestream-keyboard mt-1" : "mt-5"}`} style={{ '--keyboard-gap': compactLayout ? '3px' : '6px' } as React.CSSProperties}>
      <div className="keyboard-rows flex flex-col gap-[var(--keyboard-gap)] mx-auto">
        {KEYBOARD_ROWS.map((row, rowIndex) => (
          <div key={rowIndex} className="keyboard-row flex justify-center gap-[var(--keyboard-gap)] w-full">
            {rowIndex === 1 && <div className="flex-[0.5]"></div>}
            {rowIndex === 2 && (
              <Button
                variant="default"
                className={`keyboard-key p-0 flex-[1.5] min-w-0 text-xs font-semibold ${
                  isSubmitDisabled ? 'opacity-50 cursor-not-allowed' : ''
                } ${isPracticeMode ? 'bg-purple-500 hover:bg-purple-600' : ''} active:animate-key-press active:scale-90 transition-all duration-150`}
                onClick={() => {
                  trackEvent('keyboard_enter', {
                    mode: currentMode,
                    word_length: currentWord.length,
                    guess_length: currentGuess.length
                  });
                  onSubmit();
                }}
                disabled={isSubmitDisabled}
              >
                {isValidating ? "..." : (
                  <>
                    <span className="hidden sm:inline">ENTER</span>
                    <CheckCircle size={20} className="font-bold sm:hidden" />
                  </>
                )}
              </Button>
            )}
            {row.map((key) => (
              <KeyboardKey
                key={key}
                letter={key}
                status={keyboardStatus?.[key] || "empty"}
                onClick={onKeyPress}
              />
            ))}
            {rowIndex === 1 && <div className="flex-[0.5]"></div>}
            {rowIndex === 2 && (
              <Button
                variant="outline"
                className="keyboard-key p-0 flex-[1.5] min-w-0 border-gray-800 bg-gray-300 active:animate-key-press active:scale-90 active:bg-gray-100 active:shadow-[inset_0_3px_6px_rgba(0,0,0,0.4)] transition-all duration-150"
                onClick={() => {
                  trackEvent('keyboard_backspace', { mode: currentMode, guess_length: currentGuess.length });
                  onKeyPress("Backspace");
                }}
              >
                <Delete size={22} strokeWidth={3} className="text-gray-700" style={{ width: "22px", height: "22px" }}/>
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

Keyboard.displayName = 'Keyboard';
